/**
 * 模板骨架 → 报表 IR（基础版式）。
 *
 * 定位：extract 输出的骨架 JSON（merges/cells 已无损保留合并信息）是给 LLM 看的提示层；
 *      若由 LLM 手动把 cells 搬进 report-ir.json，极易漏掉「无文本的合并主格 / 空样式格」，
 *      导致生成 XML 时列合并（colspan）与行合并（rowspan）丢失（见 tests/out.log 复现：
 *      骨架 274 格中 49 个带 colspan 的合并格未被搬入 IR）。
 *
 * 本模块把骨架一次性无损转成 IR 基础版式：
 *  - 所有含 text/style/合并跨度的格原样搬入（含空合并主格），rowspan/colspan 不丢；
 *  - 候选占位格（candidate=label/data，无文本无样式无跨度）不搬——它们是给 LLM 的
 *    字段绑定提示，不是版式格；
 *  - source 固定 'template'，自动触发 requireGrid 强制校验（rows/columns 必须存在）。
 * LLM 只需在生成的基础版式上做字段绑定（把候选格 text 替换为 dataset/expression）
 * 并补充数据集/参数，即可进入 generate，合并信息从源头不再丢失。
 */
import type { ReportIR, StyleIR } from '../ir/types.js';
import type { TemplateSkeleton } from './extract.js';

export function skeletonToIr(skeleton: TemplateSkeleton): ReportIR {
  const cells = skeleton.cells
    .filter(
      (c) =>
        !!c.text ||
        (c.style && Object.keys(c.style).length > 0) ||
        (c.rowspan ?? 1) > 1 ||
        (c.colspan ?? 1) > 1,
    )
    .map((c) => {
      const cell: {
        row: number;
        col: number;
        rowspan?: number;
        colspan?: number;
        content?: { kind: 'text'; value: string };
        style?: StyleIR;
      } = { row: c.row, col: c.col };
      if (c.rowspan && c.rowspan > 1) cell.rowspan = c.rowspan;
      if (c.colspan && c.colspan > 1) cell.colspan = c.colspan;
      if (c.text) cell.content = { kind: 'text', value: c.text };
      if (c.style && Object.keys(c.style).length > 0) cell.style = c.style as StyleIR;
      return cell;
    })
    .sort((a, b) => a.row - b.row || a.col - b.col);

  return {
    source: 'template',
    // 模板导入产物为已有的静态版式（套打/单据），本质是主从明细布局，不含交叉表语义；
    // 显式声明 'master-detail'，从源头否决 structureCheck 的 isCrosstab 硬触发
    // （交叉表必须是显式 reportType:'crosstab'）。
    reportType: 'master-detail',
    rowCount: skeleton.sheet.rows,
    columnCount: skeleton.sheet.cols,
    rows: skeleton.rows.map((r) => ({ num: r.num, height: r.height })),
    columns: skeleton.columns.map((c) => ({ num: c.num, width: c.width })),
    cells,
  };
}
